import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { api } from "../lib/api";
import { useAuth } from "../data/authStore";
import { roleAllowed } from "../lib/roles";

/* ═══════════════════════════════════════════════════════════════════
   useSampleData — caller-scoped sample-data substrate
   --------------------------------------------------------------------
   Lightweight provider + hook that:
     1. Fetches the list of every demo record in the caller's workspace
        once per session (steward-only — admins + directors).
     2. Exposes O(1) lookups (`isDemoContact / isDemoProperty / isDemoDeal`).
     3. Surfaces a `clear()` action that calls the canonical
        `/api/workspace/clear-sample-data` endpoint and refreshes refs.
     4. Manages the dismissible banner state via `localStorage`
        (per-session dismissal — returns automatically when sample data
        re-appears, e.g. after a fresh seed).

   Non-stewards never trigger the network call — the provider gracefully
   no-ops for them so consuming components stay drop-in safe everywhere.
   ─────────────────────────────────────────────────────────────────── */

const STORAGE_DISMISS_KEY = "chainflow:sample-data-banner-dismissed-v1";

const SampleDataContext = createContext({
  loading: false,
  hasSampleData: false,
  counts: { contacts: 0, properties: 0, deals: 0 },
  isDemoContact: () => false,
  isDemoProperty: () => false,
  isDemoDeal: () => false,
  refresh: async () => {},
  clear: async () => ({ ok: false, summary: null }),
  bannerDismissed: false,
  dismissBanner: () => {},
  resetBannerDismiss: () => {},
  isSteward: false,
});

export const useSampleData = () => useContext(SampleDataContext);

export const SampleDataProvider = ({ children }) => {
  const { user } = useAuth();
  const isSteward = !!user && roleAllowed(user.role, "director_nav");

  const [refs, setRefs] = useState({
    has_sample_data: false,
    demo_contact_ids: [],
    demo_property_ids: [],
    demo_deal_ids: [],
    counts: { contacts: 0, properties: 0, deals: 0 },
  });
  const [loading, setLoading] = useState(false);

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const cancelled = useRef(false);

  const fetchRefs = useCallback(async () => {
    if (!isSteward) {
      setRefs({
        has_sample_data: false,
        demo_contact_ids: [],
        demo_property_ids: [],
        demo_deal_ids: [],
        counts: { contacts: 0, properties: 0, deals: 0 },
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/api/workspace/sample-data-refs");
      if (cancelled.current) return;
      setRefs({
        has_sample_data: !!data?.has_sample_data,
        demo_contact_ids: Array.isArray(data?.demo_contact_ids) ? data.demo_contact_ids : [],
        demo_property_ids: Array.isArray(data?.demo_property_ids) ? data.demo_property_ids : [],
        demo_deal_ids: Array.isArray(data?.demo_deal_ids) ? data.demo_deal_ids : [],
        counts: data?.counts || { contacts: 0, properties: 0, deals: 0 },
      });
    } catch {
      // Calm failure — never block the UI. Non-stewards may transiently
      // hit this if their role flips mid-session; leave refs at default.
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, [isSteward]);

  useEffect(() => {
    cancelled.current = false;
    fetchRefs();
    return () => {
      cancelled.current = true;
    };
  }, [fetchRefs]);

  const contactSet = useMemo(() => new Set(refs.demo_contact_ids), [refs.demo_contact_ids]);
  const propertySet = useMemo(() => new Set(refs.demo_property_ids), [refs.demo_property_ids]);
  const dealSet = useMemo(() => new Set(refs.demo_deal_ids), [refs.demo_deal_ids]);

  const isDemoContact = useCallback((id) => !!id && contactSet.has(id), [contactSet]);
  const isDemoProperty = useCallback((id) => !!id && propertySet.has(id), [propertySet]);
  const isDemoDeal = useCallback((id) => !!id && dealSet.has(id), [dealSet]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_DISMISS_KEY, "1");
    } catch {
      /* noop */
    }
  }, []);

  const resetBannerDismiss = useCallback(() => {
    setBannerDismissed(false);
    try {
      window.localStorage.removeItem(STORAGE_DISMISS_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const clear = useCallback(async () => {
    if (!isSteward) return { ok: false, summary: "Sample-data clear is steward-only." };
    setLoading(true);
    try {
      const { data } = await api.post("/api/workspace/clear-sample-data");
      // Refresh refs after clear; reset dismissal so the banner can
      // surface again if data is re-seeded later.
      resetBannerDismiss();
      await fetchRefs();
      // Fan out a workspace-cleared signal so chainStore (and anything
      // else holding deal/property arrays) can drop stale IDs and
      // re-hydrate from the canonical /api/deals and /api/properties.
      // This is the fix for the post-clear "deal not found" stage
      // update bug observed on mobile Safari / iPad.
      try { window.dispatchEvent(new Event("cf:workspace-cleared")); }
      catch { /* SSR / old Safari — fine */ }
      return { ok: true, summary: data?.summary || "Sample data cleared." };
    } catch (err) {
      return {
        ok: false,
        summary: err?.response?.data?.detail || "Could not clear sample data.",
      };
    } finally {
      setLoading(false);
    }
  }, [isSteward, fetchRefs, resetBannerDismiss]);

  const value = useMemo(
    () => ({
      loading,
      hasSampleData: refs.has_sample_data,
      counts: refs.counts,
      isDemoContact,
      isDemoProperty,
      isDemoDeal,
      refresh: fetchRefs,
      clear,
      bannerDismissed,
      dismissBanner,
      resetBannerDismiss,
      isSteward,
    }),
    [
      loading,
      refs.has_sample_data,
      refs.counts,
      isDemoContact,
      isDemoProperty,
      isDemoDeal,
      fetchRefs,
      clear,
      bannerDismissed,
      dismissBanner,
      resetBannerDismiss,
      isSteward,
    ],
  );

  return <SampleDataContext.Provider value={value}>{children}</SampleDataContext.Provider>;
};

export default useSampleData;
